import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { authToken } from '../globals/Global';
import { getAllTemples, createTemple, updateTemple, deleteTemple, importTempleCSV, getAllPurohitProfiles } from '../globals/Services';

const getAuthToken = () => authToken || localStorage.getItem('sessionToken');

const emptyForm = {
  userId: 1,
  name: '',
  description: '',
  mainDeityName: '',
  city: '',
  state: '',
  addressLine1: '',
  addressLine2: '',
  openingTime: '',
  closingTime: '',
  contactPersonName: '',
  contactPersonMobileNo: '',
  purohitUserId: '',
  purohitId: '',
  establishedYear: '',
};

export default function TempleManagement() {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemple, setSelectedTemple] = useState(null);
  const [refetch, setRefetch] = useState(0);
  const triggerRefetch = () => setRefetch(p => p + 1);

  const [formData, setFormData] = useState(emptyForm);
  const [imageFiles, setImageFiles] = useState([]);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingTemple, setDeletingTemple] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCSV, setUploadingCSV] = useState(false);

  // --- Purohit list state ---
  const [purohitList, setPurohitList] = useState([]);
  const [purohitLoading, setPurohitLoading] = useState(false);

  // --- Fetch Purohits ---
  const fetchPurohits = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      console.warn('fetchPurohits: no auth token yet, skipping');
      return;
    }
    setPurohitLoading(true);
    try {
      console.log('Fetching purohits with token:', token);
      const res = await fetch(getAllPurohitProfiles, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch purohits (${res.status})`);
      const result = await res.json();
      console.log('Purohit API raw response:', result);
      // Handle all common Spring Boot response shapes
      let list = [];
      if (Array.isArray(result)) {
        list = result;
      } else if (Array.isArray(result?.data)) {
        list = result.data;
      } else if (Array.isArray(result?.content)) {
        list = result.content;
      } else if (Array.isArray(result?.purohits)) {
        list = result.purohits;
      } else if (Array.isArray(result?.result)) {
        list = result.result;
      } else {
        console.warn('Purohit API: unexpected response shape', result);
      }
      console.log('Purohit list parsed:', list);
      setPurohitList(list);
    } catch (err) {
      console.error('Purohit fetch error:', err);
      toast.error(`Error fetching purohit list: ${err.message}`);
    } finally {
      setPurohitLoading(false);
    }
  }, []);

  // --- Fetch Temples ---
  const fetchTemples = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getAllTemples, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Failed to fetch temples');
      const result = await res.json();
      setTemples(result?.data || []);
    } catch (err) {
      toast.error('Error fetching temples');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemples();
  }, [refetch, fetchTemples]);

  // Fetch purohits whenever a dialog opens (token is guaranteed ready by then)
  useEffect(() => {
    const token = getAuthToken();
    if ((openAddDialog || openEditDialog) && token) {
      fetchPurohits();
    }
  }, [openAddDialog, openEditDialog, fetchPurohits]);

  // --- CSV Upload ---
  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a valid CSV file');
      event.target.value = '';
      return;
    }

    setUploadingCSV(true);
    try {
      const fd = new FormData();
      fd.append('file', file);

      const res = await fetch(importTempleCSV, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: fd,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'CSV upload failed');

      toast.success('CSV uploaded successfully!');
      triggerRefetch();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setUploadingCSV(false);
      event.target.value = '';
    }
  };

  // --- Create ---
  const handleCreate = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('temple', JSON.stringify({ ...formData, userId: 1 }));
      imageFiles.forEach(f => fd.append('images', f));

      const res = await fetch(createTemple, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        body: fd,
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Create failed');

      toast.success('Temple created successfully!');
      setOpenAddDialog(false);
      setFormData(emptyForm);
      setImageFiles([]);
      triggerRefetch();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Update ---
  const handleUpdate = async () => {
    if (!selectedTemple) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('temple', JSON.stringify(formData));
      imageFiles.forEach(f => fd.append('images', f));

      const token = getAuthToken();
      const res = await fetch(updateTemple(selectedTemple.id), {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const text = await res.text();
      const result = text ? JSON.parse(text) : {};

      if (!res.ok) throw new Error(result.message || `Update failed (${res.status})`);

      toast.success('Temple updated successfully!');
      setOpenEditDialog(false);
      setSelectedTemple(null);
      setFormData(emptyForm);
      setImageFiles([]);
      triggerRefetch();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // --- Delete ---
  const handleDelete = async () => {
    if (!deletingTemple) return;
    setSubmitting(true);
    try {
      const res = await fetch(deleteTemple(deletingTemple.id), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      if (!res.ok) {
        const result = await res.json();
        throw new Error(result.message || 'Delete failed');
      }
      toast.success('Temple deleted successfully!');
      setOpenDeleteDialog(false);
      setDeletingTemple(null);
      triggerRefetch();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (temple) => {
    setSelectedTemple(temple);
    setFormData({
      userId: temple.userId || 1,
      name: temple.name || '',
      description: temple.description || '',
      mainDeityName: temple.mainDeityName || '',
      city: temple.city || '',
      state: temple.state || '',
      addressLine1: temple.addressLine1 || '',
      addressLine2: temple.addressLine2 || '',
      openingTime: temple.openingTime || '',
      closingTime: temple.closingTime || '',
      contactPersonName: temple.contactPersonName || '',
      contactPersonMobileNo: temple.contactPersonMobileNo || '',
      purohitUserId: temple.purohitUserId || '',
      purohitId: temple.purohitId || '',
      establishedYear: temple.establishedYear || '',
    });
    setImageFiles([]);
    setOpenEditDialog(true);
  };

  // Helper: get display label for a purohit entry
  // Covers common user-profile API field shapes (firstName/lastName, name, userName, etc.)
  const getPurohitLabel = (p) => {
    const firstName = p.firstName || p.first_name || '';
    const lastName = p.lastName || p.last_name || '';
    const combined = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName;
    const name = combined || p.name || p.purohitName || p.fullName || p.userName || p.username || `Purohit #${p.id}`;
    const phone = p.mobileNo || p.mobile || p.phoneNo || p.contactNo || p.phone || '';
    return phone ? `${name} — ${phone}` : name;
  };

  const TempleFormFields = () => (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Temple Name *"
          value={formData.name}
          onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Main Deity Name"
          value={formData.mainDeityName}
          onChange={e => setFormData(p => ({ ...p, mainDeityName: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Description"
          multiline
          rows={2}
          value={formData.description}
          onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="City"
          value={formData.city}
          onChange={e => setFormData(p => ({ ...p, city: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="State"
          value={formData.state}
          onChange={e => setFormData(p => ({ ...p, state: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Address Line 1"
          value={formData.addressLine1}
          onChange={e => setFormData(p => ({ ...p, addressLine1: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Address Line 2"
          value={formData.addressLine2}
          onChange={e => setFormData(p => ({ ...p, addressLine2: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Opening Time (e.g. 05:00 AM)"
          value={formData.openingTime}
          onChange={e => setFormData(p => ({ ...p, openingTime: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Closing Time (e.g. 09:00 PM)"
          value={formData.closingTime}
          onChange={e => setFormData(p => ({ ...p, closingTime: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Person Name"
          value={formData.contactPersonName}
          onChange={e => setFormData(p => ({ ...p, contactPersonName: e.target.value }))}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Person Mobile"
          value={formData.contactPersonMobileNo}
          onChange={e => setFormData(p => ({ ...p, contactPersonMobileNo: e.target.value }))}
        />
      </Grid>

      {/* Purohit Dropdown — replaces Pujari Name, Pujari Phone, Purohit User ID */}
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          label="Assign Purohit"
          value={formData.purohitId || ''}
          onChange={e => {
            const selected = purohitList.find(p => String(p.id) === String(e.target.value));
            setFormData(prev => ({
              ...prev,
              purohitId: e.target.value,
              // Also populate purohitUserId from the selected purohit if available
              purohitUserId: selected?.userId || selected?.purohitUserId || e.target.value,
            }));
          }}
          disabled={purohitLoading}
          InputProps={{
            endAdornment: purohitLoading ? (
              <CircularProgress size={18} sx={{ mr: 2 }} />
            ) : null,
          }}
          SelectProps={{ displayEmpty: true }}
        >
          <MenuItem value="">
            <em>{purohitLoading ? 'Loading purohits...' : 'Select a Purohit'}</em>
          </MenuItem>
          {purohitList.map(p => (
            <MenuItem key={p.id} value={String(p.id)}>
              {getPurohitLabel(p)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Established Year"
          value={formData.establishedYear}
          onChange={e => setFormData(p => ({ ...p, establishedYear: e.target.value }))}
        />
      </Grid>

      {/* Image Upload */}
      <Grid item xs={12}>
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Temple Images (Optional)
          </Typography>
          <Button variant="outlined" component="label" size="small">
            Choose Images
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={e => setImageFiles(Array.from(e.target.files))}
            />
          </Button>
          {imageFiles.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {imageFiles.map((f, i) => (
                <Chip key={i} label={f.name} size="small" color="success" variant="outlined" />
              ))}
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="700">
          Temple Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            component="label"
            disabled={uploadingCSV}
            startIcon={uploadingCSV ? <CircularProgress size={18} /> : <UploadFileIcon />}
            sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none', px: 3 }}
          >
            {uploadingCSV ? 'Uploading...' : 'Upload CSV'}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={handleCSVUpload}
              disabled={uploadingCSV}
            />
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => { setFormData(emptyForm); setImageFiles([]); setOpenAddDialog(true); }}
            sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none', px: 3 }}
          >
            + Add Temple
          </Button>
        </Box>
      </Box>

      {/* Scrollable List */}
      <Box sx={{ height: 'calc(100vh - 140px)', overflow: 'auto', pr: 0.5 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" mt={8}>
            <CircularProgress />
          </Box>
        ) : temples.length === 0 ? (
          <Typography sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
            No temples found.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {temples.map((temple, index) => (
              <Box
                key={temple.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: index === 0 ? 'primary.main' : 'grey.200',
                  borderRadius: 2,
                  bgcolor: 'white',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                  },
                }}
              >
                {/* Temple Image */}
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: 2,
                    overflow: 'hidden',
                    flexShrink: 0,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {temple.images?.[0] ? (
                    <img
                      src={temple.images[0]}
                      alt={temple.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography fontSize={32}>🛕</Typography>
                  )}
                </Box>

                {/* Info */}
                <Box flexGrow={1} minWidth={0}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    ID: {temple.id}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {temple.name}
                  </Typography>
                  {temple.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Description: {temple.description}
                    </Typography>
                  )}
                  <Box display="flex" flexWrap="wrap" gap={1.5} mt={0.4}>
                    {temple.mainDeityName && (
                      <Typography variant="body2" color="text.secondary">
                        🙏 {temple.mainDeityName}
                      </Typography>
                    )}
                    {(temple.city || temple.state) && (
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <LocationOnIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {[temple.city, temple.state].filter(Boolean).join(', ')}
                        </Typography>
                      </Box>
                    )}
                    {(temple.openingTime || temple.closingTime) && (
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {temple.openingTime} – {temple.closingTime}
                        </Typography>
                      </Box>
                    )}
                    {temple.pujariName && (
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <PersonIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {temple.pujariName}
                        </Typography>
                      </Box>
                    )}
                    {temple.contactPersonMobileNo && (
                      <Box display="flex" alignItems="center" gap={0.3}>
                        <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {temple.contactPersonMobileNo}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Actions */}
                <Box display="flex" flexDirection="column" gap={0.5} flexShrink={0}>
                  <IconButton size="small" color="primary" onClick={() => openEdit(temple)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => { setDeletingTemple(temple); setOpenDeleteDialog(true); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* --- Add Dialog --- */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Add New Temple</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <TempleFormFields />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitting || !formData.name}>
            {submitting ? 'Creating...' : 'Create Temple'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Edit Dialog --- */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Temple — {selectedTemple?.name}</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <TempleFormFields />
          {selectedTemple?.images?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Current Images</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {selectedTemple.images.map((url, i) => (
                  <img key={i} src={url} alt={`img-${i}`} style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* --- Delete Dialog --- */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingTemple?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={submitting}>
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </Box>
  );
}